import { useEffect, useRef } from 'react';

import type { Pageview } from '../services/Pageview';
import Tracking from '../services/Tracking';

export function useOneTimeTracking<A extends UnsafeAny[]>(
  track: ((...args: A) => void) | (() => void),
): (...args: A) => void {
  if (process.env.NODE_ENV !== 'production') {
    console.info('=== track - useOneTimeTracking', { track });
  }
  const ref = useRef(false);

  const wrapper: ((...args: A) => void) | (() => void) = (...args: A) => {
    if (ref.current) return;
    track(...args);
    ref.current = true;
  };

  return wrapper;
}

export function useOnLoadTracking(track: () => void, deps: UnsafeAny[] = []): void {
  if (process.env.NODE_ENV !== 'production') {
    console.info('=== track - useOnLoadTracking', { track, deps });
  }
  const ref = useRef(false);
  useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    track();
  }, deps);
}

export function usePageView(page: Pageview, ...args: UnsafeAny[]) {
  if (process.env.NODE_ENV !== 'production') {
    console.info('=== track - usePageView', { page, args });
  }
  if (typeof page !== 'string') return;
  useOnLoadTracking(() => Tracking.pageview(page, ...args));
}

export function useModalView(page: Pageview, ...args: UnsafeAny[]) {
  if (process.env.NODE_ENV !== 'production') {
    console.info('=== track - useModalView', { page, args });
  }
  if (typeof page !== 'string') return;
  useOnLoadTracking(() => Tracking.modalView(page, ...args));
}

export function useTimedTracking<A extends UnsafeAny[]>(
  track: ((...args: A) => void) | (() => void),
  timeout: number,
): (...args: A) => void {
  if (process.env.NODE_ENV !== 'production') {
    console.info('=== useTimedTracking', { track, timeout });
  }
  const ref = useRef(null);
  const handler = useRef(null);

  const wrapper: ((...args: A) => void) | (() => void) = (...args: A) => {
    if (ref.current) {
      clearTimeout(ref.current);
    }
    handler.current = () => track(...args);
    ref.current = setTimeout(() => {
      ref.current = null;
      handler.current();
    }, timeout);
  };

  useEffect(() => {
    return () => {
      if (ref.current && handler.current) {
        clearTimeout(ref.current);
        handler.current();
      }
    };
  }, []);

  return wrapper;
}
